import React, { useState, useEffect } from 'react';
import { Library, User } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = async () => {
  if (typeof chrome !== 'undefined' && chrome.storage) {
    const { accessToken } = await chrome.storage.local.get('accessToken');
    if (accessToken && accessToken !== 'session_active') {
      return { Authorization: `Bearer ${accessToken}` };
    }
  }
  return {};
};

export default function SharedPromptsTab() {
  const [sharedPrompts, setSharedPrompts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSharedPrompts();
  }, []);

  const fetchSharedPrompts = async () => {
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const { data } = await axios.get(`${API_URL}/api/shared`, {
        withCredentials: true,
        headers
      });
      setSharedPrompts(data);
    } catch (error) {
      console.error('Error fetching shared prompts:', error);
      toast.error('Failed to load shared prompts');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (content) => {
    navigator.clipboard.writeText(content);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2C4C3B] mx-auto"></div>
      </div>
    );
  }

  if (sharedPrompts.length === 0) {
    return (
      <div className="text-center py-8" data-testid="empty-shared-prompts">
        <Library size={48} className="mx-auto text-[#E7E5E4] mb-3" />
        <p className="text-sm text-[#78716C]">No shared prompts yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="shared-prompts-tab">
      {sharedPrompts.map((prompt) => (
        <div
          key={`${prompt.prompt_id}-${prompt.shared_by?.user_id}`}
          className="bg-white rounded-xl border border-[#E7E5E4] shadow-sm overflow-hidden flex flex-col p-4"
          data-testid={`shared-prompt-${prompt.prompt_id}`}
        >
          {/* Shared by info */}
          <div className="flex items-center gap-2 mb-2 text-xs text-[#78716C]">
            <User size={12} />
            <span>Shared by {prompt.shared_by?.name}</span>
          </div>

          {/* Title */}
          <h3 className="text-lg tracking-tight font-medium text-[#1C1917] mb-2">{prompt.title}</h3>

          {/* Tags */}
          <div className="flex flex-wrap gap-1 mb-3">
            {prompt.function_tag && (
              <span className="inline-flex items-center rounded-md px-2 py-1 text-[10px] font-medium tracking-wide uppercase bg-[#EAF0EC] text-[#2C4C3B]">
                {prompt.function_tag}
              </span>
            )}
            {prompt.category_tag && (
              <span className="inline-flex items-center rounded-md px-2 py-1 text-[10px] font-medium tracking-wide uppercase bg-[#F5F2EA] text-[#78716C]">
                {prompt.category_tag}
              </span>
            )}
            {prompt.model_tag && (
              <span className="inline-flex items-center rounded-md px-2 py-1 text-[10px] font-medium tracking-wide uppercase bg-[#EAF0EC] text-[#2C4C3B]">
                {prompt.model_tag}
              </span>
            )}
          </div>

          {/* Content */}
          <p className="text-sm leading-relaxed text-[#57534E] mb-3">{prompt.content}</p>

          {/* Commentary */}
          {prompt.commentary && (
            <p className="text-xs text-[#78716C] italic mb-3">{prompt.commentary}</p>
          )}

          {/* Copy button */}
          <button
            onClick={() => handleCopy(prompt.content)}
            className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium transition-colors border border-[#E7E5E4] bg-white text-[#1C1917] hover:bg-[#FAFAF9] w-full"
            data-testid="copy-shared-prompt-button"
          >
            Copy Prompt
          </button>
        </div>
      ))}
    </div>
  );
}